import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';

import toSecondaryParentRows from '../format/toSecondaryParentRows';
import getTermedHistogramResponses from './getTermedHistogramResponses';
import getTopNTermsResponses from './getTopNTermsResponses';
import getExcludeTopNTermResponses from './getExcludeTopNTermResponses';
import getAllCurrentAndPreviousTermResponses from './getAllCurrentAndPreviousTermResponses';

// Where the stacked secondary rows' subtotals sit in the responses (below)
export const SUBTOTALS_RESPONSE_INDEX = 4;
export const PREVIOUS_SUBTOTALS_RESPONSE_INDEX = 5;

interface FetchMatrixResponsesArgs {
  chartDef: V5ChartDefinition;
  filterInput: FilterInput;
  usedMetrics: Metrics.Metric[];
  isDashboardGadget?: boolean;
  currentDateScope: DateRangeInput;
  previousDateScope: DateRangeInput;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  comparison?: PersistedComparisonType;
  client: ApolloClient<NormalizedCacheObject>;
  groupByField: string;
  isTermedHistogram: boolean;
  isTopN: boolean;
  isExcludeTopN: boolean;
  isSecondaryRowsStacked?: boolean;
}

/**
 * Dispatches to the appropriate fetch strategy based on the matrix
 * mode flags, and returns the raw responses array.
 *
 * Response layout (indices):
 *   [0] current grouped data
 *   [1] current totals
 *   [2] previous grouped data  (may be empty)
 *   [3] previous totals        (may be empty)
 *   [4] subtotals per dimension A value (stacked secondary rows only)
 *   [5] previous subtotals per dimension A value (stacked secondary rows
 *       with a column per metric only; may be empty)
 */
const fetchMatrixResponses = async ({
  chartDef,
  filterInput,
  usedMetrics,
  isDashboardGadget,
  currentDateScope,
  previousDateScope,
  toMetricInput,
  comparison,
  client,
  groupByField,
  isTermedHistogram,
  isTopN,
  isExcludeTopN,
  isSecondaryRowsStacked,
}: FetchMatrixResponsesArgs): Promise<MetricsResponse[]> => {
  if (isTermedHistogram) {
    return getTermedHistogramResponses({
      chartDef,
      filterInput,
      usedMetrics,
      isDashboardGadget,
      currentDateScope,
      toMetricInput,
      client,
      groupByField,
      isSecondaryRowsStacked,
    });
  }

  const responses = await (() => {
    if (isTopN) {
      return getTopNTermsResponses({
        chartDef,
        filterInput,
        usedMetrics,
        isDashboardGadget,
        currentDateScope,
        previousDateScope,
        toMetricInput,
        client,
        groupByField,
        comparison,
      });
    }
    if (isExcludeTopN) {
      return getExcludeTopNTermResponses({
        chartDef,
        filterInput,
        usedMetrics,
        isDashboardGadget,
        currentDateScope,
        previousDateScope,
        toMetricInput,
        client,
        groupByField,
        comparison,
      });
    }
    return getAllCurrentAndPreviousTermResponses({
      chartDef,
      filterInput,
      usedMetrics,
      isDashboardGadget,
      currentDateScope,
      toMetricInput,
      previousDateScope,
      client,
      groupByField,
      comparison,
    });
  })();

  // Stacked secondary rows with a column per metric: each dimension A value's
  // own row is the row its secondary terms are nested in, so they need no
  // query of their own
  if (
    isSecondaryRowsStacked &&
    !chartDef.dimensionB &&
    isSecondaryDimensionAInEffect(chartDef)
  ) {
    const [rows, totals, previousRows = [], previousTotals = []] = responses;
    return [
      rows,
      totals,
      previousRows,
      previousTotals,
      toSecondaryParentRows(rows),
      toSecondaryParentRows(previousRows),
    ];
  }

  return responses;
};

export default fetchMatrixResponses;
